import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { images } from '../utils/images';
import VideoBackground from './VideoBackground';

const WelcomeSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="about"
      ref={sectionRef}
      className={`py-20 bg-white transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Featured Image Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={images.about}
              alt="Life Care and Wellness NDIS support team helping participants"
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold"
            style={{ color: '#587740' }}
          >
            Welcome to Life Care and Wellness
          </h2>
          
          <p 
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: '#5e5e5e' }}
          >
            Are you happy with your current supports? If something doesn't feel right — if you feel unheard, overlooked, or like you're just a number — trust that feeling. You deserve better, and we're here to show you what "better" actually looks like.
          </p>

          <p 
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: '#5e5e5e' }}
          >
            We're committed to providing compassionate, personalised care that empowers you to achieve your goals and live the life you choose. In simple terms — we listen first, then we act. We treat you like family, not a file.
          </p>

          <div 
            className="p-6 rounded-xl mx-auto max-w-2xl"
            style={{ backgroundColor: '#81b95610' }}
          >
            <p className="text-base font-semibold" style={{ color: '#587740' }}>
              We offer free consultations and a free action plan based on your current NDIS plan. No strings attached — just a genuine conversation about how we can help.
            </p>
          </div>

          <p 
            className="text-base leading-relaxed italic"
            style={{ color: '#6c935e' }}
          >
            We see the psychological struggles that go unseen. You are heard and valued and in control.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => scrollToSection('services')}
              className="text-white hover:opacity-90 transition-opacity text-base px-8 py-6"
              style={{ backgroundColor: '#81b956' }}
            >
              Explore Our Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => window.location.href = '/get-on-ndis-free'}
              className="border-2 text-base px-8 py-6"
              style={{ borderColor: '#81b956', color: '#81b956' }}
            >
              Get on the NDIS Free
            </Button>
          </div>
        </div>

        {/* Stats Section */}
        <VideoBackground variant="dark" className="mt-16 rounded-2xl max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-12 px-6">
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold mb-2 text-white">
                500+
              </div>
              <p className="text-lg text-white/90">
                People We've Supported
              </p>
            </div>
          
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold mb-2 text-white">
                15+
              </div>
              <p className="text-lg text-white/90">
                Years of Genuine Care
              </p>
            </div>
          
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold mb-2 text-white">
                100%
              </div>
              <p className="text-lg text-white/90">
                You Come First
              </p>
            </div>
          </div>
        </VideoBackground>
      </div>
    </section>
  );
};

export default WelcomeSection;
